/**
 * Processa dados da API para o formato do frontend
 * @param {Array} contas - Array de contas vindas da API
 * @returns {Array} Array de contas processadas
 */
export const processarContas = (contas) => {
  return contas.map(conta => {
    const rawVencimento =
      conta?.data_vencimento ??
      conta?.dataVencimento ??
      conta?.vencimento ??
      conta?.data_vencimento_convertida ??
      null;

    const parseVencimento = (value) => {
      if (!value) return { date: null, br: null };
      const str = String(value).trim();

      // DD/MM/YYYY
      const brMatch = str.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (brMatch) {
        const [, d, m, y] = brMatch;
        const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        return { date: isNaN(date.getTime()) ? null : date, br: `${d}/${m}/${y}` };
      }

      // YYYY-MM-DD
      const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, y, m, d] = isoMatch;
        const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
        return { date: isNaN(date.getTime()) ? null : date, br: `${d}/${m}/${y}` };
      }

      // Fallback: tentar Date() nativo
      const date = new Date(str);
      if (!isNaN(date.getTime())) {
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = String(date.getFullYear());
        return { date, br: `${d}/${m}/${y}` };
      }

      return { date: null, br: null };
    };

    const { date: parsedDate, br: vencimentoBr } = parseVencimento(rawVencimento);
    const dataVencimento = parsedDate || new Date(8640000000000000); // manter inválidos no final
    
    // Normalizar hoje para meia-noite para comparação correta
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    if (parsedDate) {
      dataVencimento.setHours(0, 0, 0, 0);
    }
    
    // Verificar se a data é válida e calcular dias de vencimento apenas para contas em aberto
    const isDataValida = !!parsedDate && !isNaN(parsedDate.getTime());
    const diasVencimento = (isDataValida && conta.status === 'A') ? Math.ceil((dataVencimento - hoje) / (1000 * 60 * 60 * 24)) : 0;
    
    // Determinar status baseado no campo status da API
    let status = 'proximos_meses';
    
    if (conta.status === 'F') {
      status = 'paga';
    } else if (conta.status === 'C') {
      status = 'cancelada';
    } else if (conta.status === 'P') {
      status = 'paga_parcialmente';
    } else if (conta.status === 'A') {
      // Para contas não pagas, usar data de vencimento
      if (diasVencimento < 0) {
        status = 'vencida';
      } else if (diasVencimento === 0) {
        status = 'hoje';
      } else if (diasVencimento <= 7) {
        status = 'proxima_vencimento';
      } else {
        // Verificar se vence ainda neste mês
        const mesVencimento = dataVencimento.getMonth();
        const anoVencimento = dataVencimento.getFullYear();
        const mesAtual = hoje.getMonth();
        const anoAtual = hoje.getFullYear();
        
        // Se vence ainda neste mês, usar 'proxima_vencimento' em vez de 'proximos_meses'
        if (mesVencimento === mesAtual && anoVencimento === anoAtual) {
          status = 'proxima_vencimento';
        }
        // Caso contrário, mantém 'proximos_meses' (vence em meses futuros)
      }
    }

    return {
      id: conta.id ?? conta.codigo ?? null,
      unidade: conta.unidade ?? null,
      fornecedor: conta.fornecedor ?? conta?.pessoa?.nome ?? null,
      observacao: conta.observacao ?? conta.observacoes ?? conta.obs ?? null,
      descricao: (conta.observacao ?? conta.observacoes ?? conta.obs) || conta.conta_analitica,
      valor: parseFloat(conta.valor),
      vencimento: vencimentoBr ?? String(rawVencimento ?? ''),
      dataVencimento: dataVencimento, // Adicionar objeto Date para ordenação
      status: status,
      conta_analitica: conta.conta_analitica, // Preservar campo original
      id_conta_analitica: conta.id_conta_analitica, // ID da conta analítica
      // Ajuste: planejamento é a categoria macro e conta_analitica é a subcategoria
      categoria: conta.planejamento || 'Sem planejamento',
      subcategoria: conta.conta_analitica || 'Sem subcategoria',
      planejamento: conta.planejamento, // Campo de planejamento
      id_planejamento: conta.id_planejamento, // ID do planejamento
      prioridade: (conta.status === 'A' && diasVencimento < 0) ? 'alta' : 
                 (conta.status === 'A' && diasVencimento <= 7) ? 'media' : 'baixa',
      diasVencimento: diasVencimento,
      documento: conta.documento,
      tipo_pagamento: conta.tipo_pagamento,
      codigo_barras: conta.codigo_barras || null, // Código de barras (para boleto)
      chave_pix: conta.chave_pix || null, // Chave PIX (para PIX)
      conta: conta.conta, // Campo conta bancária
      status_auditoria: conta.status_auditoria || null, // Status de auditoria
      valor_aberto: parseFloat(conta.valor_aberto ?? 0),
      valor_pago: parseFloat(conta.valor_pago ?? 0),
      data_pagamento: conta.data_pagamento || null, // Data de pagamento
      // Adicionar campo dataPagamento (Date) para ordenação
      dataPagamento: (() => {
        if (!conta.data_pagamento) return null;
        try {
          const dataStr = String(conta.data_pagamento).trim();
          // Verificar se está no formato brasileiro "dd/mm/yyyy" ou "dd/mm/yyyy hh:mm:ss"
          const matchBrasileiro = dataStr.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?/);
          if (matchBrasileiro) {
            const [, dia, mes, ano, hora = '00', minuto = '00', segundo = '00'] = matchBrasileiro;
            const dateObj = new Date(`${ano}-${mes}-${dia}T${hora}:${minuto}:${segundo}`);
            if (!isNaN(dateObj.getTime())) {
              return dateObj;
            }
          }
          // Tentar parsear como ISO
          const dateISO = new Date(dataStr);
          if (!isNaN(dateISO.getTime())) {
            return dateISO;
          }
          return null;
        } catch {
          return null;
        }
      })(),
      logs: conta.logs || null, // Preservar campo logs (pode ser string JSON ou null) - mantido para compatibilidade
      arquivos: (() => {
        // Processar campo arquivos - pode vir como string JSON ou array
        if (!conta.arquivos) return [];
        if (Array.isArray(conta.arquivos)) {
          // Filtrar arquivos inválidos (com campos null/undefined)
          return conta.arquivos.filter(arq => arq && arq.arquivo && arq.id);
        }
        try {
          const parsed = JSON.parse(conta.arquivos);
          if (Array.isArray(parsed)) {
            // Filtrar arquivos inválidos (com campos null/undefined)
            return parsed.filter(arq => arq && arq.arquivo && arq.id);
          }
          return [];
        } catch {
          return [];
        }
      })()
    };
  }).sort((a, b) => {
    // Se ambas são pagas, ordenar por data de pagamento (mais antiga primeiro)
    if (a.status === 'paga' && b.status === 'paga') {
      if (a.dataPagamento && b.dataPagamento) {
        return a.dataPagamento - b.dataPagamento; // Crescente (mais antiga primeiro)
      }
      if (a.dataPagamento) return -1;
      if (b.dataPagamento) return 1;
    }
    // Ordenar por data de vencimento ascendente para outras contas
    return a.dataVencimento - b.dataVencimento;
  });
};
